/** Money is integer minor units everywhere; these helpers are the only place it becomes text. */

const nfCache = new Map<string, Intl.NumberFormat>();
function nf(key: string, make: () => Intl.NumberFormat) {
  let f = nfCache.get(key);
  if (!f) nfCache.set(key, (f = make()));
  return f;
}

export function exponent(currency: string): number {
  return nf(`exp:${currency}`, () => new Intl.NumberFormat('en-US', { style: 'currency', currency })).resolvedOptions().maximumFractionDigits ?? 2;
}

export const toMajor = (minor: number, currency = 'USD') => minor / 10 ** exponent(currency);

/** Parse a user-typed amount ("1,250.50") into minor units, or null. */
export function parseAmount(text: string, currency = 'USD'): number | null {
  const t = text.replace(/[,\s$€£¥]/g, '');
  if (!t) return null;
  if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
  const e = exponent(currency);
  const [whole, frac = ''] = t.replace('-', '').split('.');
  if (frac.length > e) return null;
  const minor = Number(whole) * 10 ** e + Number(frac.padEnd(e, '0') || 0);
  return t.startsWith('-') ? -minor : minor;
}

/** Plain decimal for inputs: 125050 → "1250.50". */
export function toInput(minor: number | null | undefined, currency = 'USD') {
  if (minor === null || minor === undefined) return '';
  return toMajor(minor, currency).toFixed(exponent(currency));
}

/** $12,345.67 (or -$12,345.67). */
export function money(minor: number | null | undefined, currency = 'USD', opts: { compact?: boolean; cents?: boolean; sign?: boolean } = {}) {
  if (minor === null || minor === undefined || Number.isNaN(minor)) return '—';
  const major = toMajor(minor, currency);
  if (opts.compact && Math.abs(major) >= 10_000) {
    return nf(`c:${currency}`, () => new Intl.NumberFormat('en-US', { style: 'currency', currency, notation: 'compact', maximumFractionDigits: 1 })).format(major);
  }
  // Compact whole amounts (axis ticks such as $0 and $5,000) read better without cents.
  const digits = opts.cents === false || (opts.compact && Number.isInteger(major)) ? 0 : exponent(currency);
  const s = nf(`m:${currency}:${digits}`, () => new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits })).format(major);
  return opts.sign && minor > 0 ? `+${s}` : s;
}

/** Accounting style for statements: 12,345.67 and (12,345.67); zero is a dash. */
/** Totals that never add across currencies: "$89,820.88 + £3,923.84". */
export function moneyByCurrency(items: { amount: number; currency: string }[], base = 'USD') {
  const by = new Map<string, number>();
  for (const i of items) by.set(i.currency, (by.get(i.currency) ?? 0) + i.amount);
  if (!by.size) return money(0, base);
  return [...by.entries()]
    .sort(([a], [b]) => (a === base ? -1 : b === base ? 1 : a.localeCompare(b)))
    .map(([c, v]) => money(v, c))
    .join(' + ');
}

export function acct(minor: number | null | undefined, currency = 'USD', opts: { cents?: boolean } = {}) {
  if (minor === null || minor === undefined) return '';
  if (minor === 0) return '–';
  const digits = opts.cents === false ? 0 : exponent(currency);
  const s = nf(`a:${digits}`, () => new Intl.NumberFormat('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })).format(Math.abs(toMajor(minor, currency)));
  return minor < 0 ? `(${s})` : s;
}

export function pct(v: number | null | undefined, digits = 1) {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${(v * 100).toFixed(digits)}%`;
}

export function num(v: number | null | undefined, digits = 0) {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return v.toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: digits });
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-30" → "Oct 30, 2026". */
export function date(d: string | Date | null | undefined, opts: { year?: boolean } = {}) {
  if (!d) return '—';
  const s = typeof d === 'string' ? d : d.toISOString();
  const [y, m, day] = s.slice(0, 10).split('-').map(Number);
  if (!y || !m || !day) return String(d);
  return opts.year === false ? `${MONTHS[m - 1]} ${day}` : `${MONTHS[m - 1]} ${day}, ${y}`;
}

/** Timestamps in the viewer's zone: "Oct 30, 3:41 PM". */
export function dateTime(d: string | Date | null | undefined) {
  if (!d) return '—';
  const x = typeof d === 'string' ? new Date(d) : d;
  return x.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function relative(d: string | Date | null | undefined) {
  if (!d) return '—';
  const x = typeof d === 'string' ? new Date(d) : d;
  const s = Math.round((Date.now() - x.getTime()) / 1000);
  if (s < -45) {
    // Future times (retry schedules, due dates)
    const f = -s;
    if (f < 3600) return `in ${Math.max(1, Math.round(f / 60))}m`;
    if (f < 86400) return `in ${Math.round(f / 3600)}h`;
    if (f < 86400 * 30) return `in ${Math.round(f / 86400)}d`;
    return dateTime(x);
  }
  if (s < 0) return 'in a moment';
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.round(s / 86400)}d ago`;
  return dateTime(x);
}

/** "2026-10" → "Oct 2026" (or "Oct '26" short). */
export function period(p: string | null | undefined, short = false) {
  if (!p) return '—';
  const [y, m] = p.split('-').map(Number);
  if (!y || !m) return p;
  return short ? `${MONTHS[m - 1]} '${String(y).slice(2)}` : `${MONTHS[m - 1]} ${y}`;
}

export function addPeriods(p: string, n: number) {
  const [y, m] = p.split('-').map(Number) as [number, number];
  const t = y * 12 + (m - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
}

export function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function titleCase(s: string | null | undefined) {
  if (!s) return '';
  return s
    .toLowerCase()
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function shortId(id: string | null | undefined) {
  if (!id) return '';
  const i = id.indexOf('_');
  return i > 0 ? id.slice(i + 1, i + 9) : id.slice(0, 8);
}

export function initials(name: string | null | undefined) {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}
